// src/theme/AppThemeProvider.jsx
import React from "react";
import { ThemeProvider, createTheme, CssBaseline, useMediaQuery } from "@mui/material";

export const ColorModeContext = React.createContext({
  mode: "light",
  toggleColorMode: () => {},
  setMode: () => {},
});

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: { main: "#2563eb" },
    secondary: { main: "#7c3aed" },
    ...(mode === "light"
      ? {
          background: { default: "#f5f7fb", paper: "#ffffff" },
          text: { primary: "#111827", secondary: "#4b5563" },
          divider: "#e5e7eb",
        }
      : {
          background: { default: "#0b1020", paper: "#0f172a" },
          text: { primary: "#e5e7eb", secondary: "#94a3b8" },
          divider: "rgba(148,163,184,0.24)",
        }),
  },
  shape: { borderRadius: 16 },
  components: {
    MuiPaper: { styleOverrides: { root: { borderRadius: 16 } } },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border:
            mode === "light"
              ? "1px solid #e5e7eb"
              : "1px solid rgba(148,163,184,.24)",
        },
      },
    },
    MuiButton: { styleOverrides: { root: { textTransform: "none", fontWeight: 600 } } },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom:
            mode === "light"
              ? "1px solid #e5e7eb"
              : "1px solid rgba(148,163,184,.24)",
        },
      },
    },
  },
});

export default function AppThemeProvider({ children }) {
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  const [mode, setMode] = React.useState(() => {
    const saved = localStorage.getItem("colorMode");
    return saved || (systemPrefersDark ? "dark" : "light");
  });

  React.useEffect(() => {
    localStorage.setItem("colorMode", mode);
    document.documentElement.setAttribute("data-color-scheme", mode);
  }, [mode]);

  const colorMode = React.useMemo(
    () => ({
      mode,
      setMode,
      toggleColorMode: () => setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    [mode]
  );

  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

// tiny helper hook
export const useColorMode = () => React.useContext(ColorModeContext);
